import React, { useState, useEffect } from 'react';
import DialogModal from '../../../components/DialogModal';
import { StyledButton } from '../../Exams/styled';
import { CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import CustomDatePicker from '../../../components/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import { PaymentConcessionType } from '../../../enums';
import { isEmpty } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import { DateWiseConcession } from '../types';
import { FormWrapper } from '../styled';

interface DateWiseConcessionModalProps {
  dateWiseConcessionType: string;
  isAddDateWiseConcessionModalOpen: boolean;
  modalType: string;
  selectedDateWiseConcession: DateWiseConcession | undefined;
  onModalClose: () => void;
  setDateWiseConcession: React.Dispatch<
    React.SetStateAction<DateWiseConcession[]>
  >;
}

const DateWiseConcessionModal = ({
  dateWiseConcessionType,
  isAddDateWiseConcessionModalOpen,
  modalType,
  selectedDateWiseConcession,
  onModalClose,
  setDateWiseConcession
}: DateWiseConcessionModalProps) => {
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    dayjs(selectedDateWiseConcession?.fromDate || '')
  );
  const [toDate, setToDate] = useState<Dayjs | null>(
    dayjs(selectedDateWiseConcession?.toDate || '')
  );
  const [amount, setAmount] = useState(selectedDateWiseConcession?.amount || 0);
  const [percentage, setPercentage] = useState(
    selectedDateWiseConcession?.percentage || 0
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (modalType === 'edit' && selectedDateWiseConcession) {
      setFromDate(
        selectedDateWiseConcession.fromDate
          ? dayjs(selectedDateWiseConcession.fromDate)
          : null
      );
      setToDate(
        selectedDateWiseConcession.toDate
          ? dayjs(selectedDateWiseConcession.toDate)
          : null
      );
      setAmount(selectedDateWiseConcession.amount || 0);
      setPercentage(selectedDateWiseConcession.percentage || 0);
    } else if (modalType === 'add') {
      setFromDate(null);
      setToDate(null);
      setAmount(0);
      setPercentage(0);
    }
  }, [selectedDateWiseConcession, modalType]);

  const resetData = () => {
    setFromDate(null);
    setToDate(null);
    setAmount(0);
    setPercentage(0);
  };

  const handleModalClose = () => {
    resetData();
    onModalClose();
  };

  const handleSave = () => {
    setIsSubmitting(true);
    const dateWiseAmount =
      dateWiseConcessionType === PaymentConcessionType.DATE_WISE_AMOUNT &&
      amount > 0
        ? amount
        : null;
    const dateWisePercentage =
      dateWiseConcessionType === PaymentConcessionType.DATE_WISE_PERCENTAGE &&
      percentage > 0
        ? percentage
        : null;
    if (modalType === 'add') {
      const newDateWiseConcession: DateWiseConcession = {
        id: `${Date.now()}`,
        fromDate: fromDate ? fromDate.format('YYYY-MM-DD') : '',
        toDate: toDate ? toDate.format('YYYY-MM-DD') : '',
        amount: dateWiseAmount,
        percentage: dateWisePercentage
      };
      setDateWiseConcession((prev: DateWiseConcession[]) => [
        ...prev,
        newDateWiseConcession
      ]);
    } else if (modalType === 'edit' && selectedDateWiseConcession?._id) {
      const updateDateWiseConcession = {
        _id: selectedDateWiseConcession._id,
        id: selectedDateWiseConcession.id,
        fromDate: fromDate ? fromDate.format('YYYY-MM-DD') : '',
        toDate: toDate ? toDate.format('YYYY-MM-DD') : '',
        amount: dateWiseAmount,
        percentage: dateWisePercentage
      };
      setDateWiseConcession((prev: DateWiseConcession[]) =>
        prev.map((item) =>
          item._id === selectedDateWiseConcession._id
            ? updateDateWiseConcession
            : item
        )
      );
    }
    handleModalClose();
    setIsSubmitting(false);
  };

  const isSaveButtonDisabled =
    isSubmitting || isEmpty(fromDate) || isEmpty(toDate);

  return (
    <DialogModal
      title={`${modalType === 'add' ? 'Add' : 'Edit'} Date Wise Concession`}
      open={isAddDateWiseConcessionModalOpen}
      onClose={handleModalClose}
      actions={
        <StyledButton onClick={handleSave} disabled={isSaveButtonDisabled}>
          {isSubmitting ? (
            <CircularProgress size={24} color='inherit' />
          ) : modalType === 'add' ? (
            'Save'
          ) : (
            'Update'
          )}
        </StyledButton>
      }
    >
      <FormWrapper>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CustomDatePicker
            label='From Date'
            onChange={(newValue: any) => {
              setFromDate(newValue);
            }}
            value={fromDate}
            width='100%'
          />
          <CustomDatePicker
            label='To Date'
            onChange={(newValue: any) => {
              setToDate(newValue);
            }}
            value={toDate}
            width='100%'
          />
        </LocalizationProvider>
        {dateWiseConcessionType ===
          PaymentConcessionType.DATE_WISE_PERCENTAGE && (
          <TextInputWithIcon
            label='Percentage'
            placeholder='Enter Percentage'
            type='tel'
            value={percentage}
            onChange={(event) =>
              setPercentage(Math.max(0, Number(event.target.value)))
            }
            hasTopLabel
            InputProps={{
              inputProps: { min: 0 }
            }}
          />
        )}
        {dateWiseConcessionType === PaymentConcessionType.DATE_WISE_AMOUNT && (
          <TextInputWithIcon
            label='Amount'
            placeholder='Enter Amount'
            type='tel'
            value={amount}
            onChange={(event) =>
              setAmount(Math.max(0, Number(event.target.value)))
            }
            hasTopLabel
            InputProps={{
              inputProps: { min: 0 }
            }}
          />
        )}
      </FormWrapper>
    </DialogModal>
  );
};

export default DateWiseConcessionModal;
